import { BackupsPage } from "./pages/backups";
import { UsagePage } from "./pages/usage";
import { MailHealthPage } from "./pages/mail";
import { MigrationsPage } from "./pages/migrations";
import { SecurityPage } from "./pages/security";
import { useQueryClient } from "@tanstack/react-query";
import { createBrowserRouter, RouterProvider } from "react-router";
import { AuthLayout, ErrorState, LoginScreen, RestrictedGate, Spinner } from "@hostdock/ui";
import { api, client, SESSION_KEY } from "./api";
import { AdminLayout } from "./layout";
import { AccountPage } from "./pages/account";
import { AdminsPage } from "./pages/admins";
import { AuditPage } from "./pages/audit";
import { CustomerDetailPage } from "./pages/customer-detail";
import { CustomersPage } from "./pages/customers";
import { DashboardPage } from "./pages/dashboard";
import { AddServerPage } from "./pages/infra/add-server";
import { ProvisioningPage } from "./pages/infra/provisioning";
import { RegionsPage } from "./pages/infra/regions";
import { ServerDetailPage } from "./pages/infra/server-detail";
import { ServersPage } from "./pages/infra/servers";
import { DatabasesPage, DomainsPage, SubscriptionsPage } from "./pages/hosting/lists";
import { PlansPage } from "./pages/hosting/plans";
import { AppInstallerAdminPage } from "./pages/hosting/app-installer";
import { WebsiteDetailPage } from "./pages/hosting/website-detail";
import { WebsitesPage } from "./pages/hosting/websites";
import { NotFoundPage } from "./pages/not-found";
import { PermissionsPage } from "./pages/permissions";
import { RolesPage } from "./pages/roles";
import { SessionsPage } from "./pages/sessions";
import { BillingPage } from "./pages/billing";
import { SettingsPage } from "./pages/settings";
import { SessionProvider, useSessionQuery, useSetSession } from "./session";

const router = createBrowserRouter([
  {
    element: <AdminLayout />,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: "infrastructure/servers", element: <ServersPage /> },
      { path: "infrastructure/servers/new", element: <AddServerPage /> },
      { path: "infrastructure/servers/:id", element: <ServerDetailPage /> },
      { path: "infrastructure/regions", element: <RegionsPage /> },
      { path: "infrastructure/provisioning", element: <ProvisioningPage /> },
      { path: "hosting/websites", element: <WebsitesPage /> },
      { path: "hosting/websites/:id", element: <WebsiteDetailPage /> },
      { path: "hosting/applications", element: <WebsitesPage apps /> },
      { path: "hosting/databases", element: <DatabasesPage /> },
      { path: "hosting/domains", element: <DomainsPage /> },
      { path: "hosting/plans", element: <PlansPage /> },
      { path: "hosting/email", element: <MailHealthPage /> },
      { path: "hosting/backups", element: <BackupsPage /> },
      { path: "hosting/migrations", element: <MigrationsPage /> },
      { path: "hosting/security", element: <SecurityPage /> },
      { path: "hosting/app-installer", element: <AppInstallerAdminPage /> },
      { path: "customers/subscriptions", element: <SubscriptionsPage /> },
      { path: "customers/usage", element: <UsagePage /> },
      { path: "customers/billing", element: <BillingPage /> },
      { path: "customers", element: <CustomersPage /> },
      { path: "customers/:id", element: <CustomerDetailPage /> },
      { path: "security/admins", element: <AdminsPage /> },
      { path: "security/roles", element: <RolesPage /> },
      { path: "security/permissions", element: <PermissionsPage /> },
      { path: "security/sessions", element: <SessionsPage /> },
      { path: "security/audit", element: <AuditPage /> },
      { path: "settings", element: <SettingsPage /> },
      { path: "account", element: <AccountPage /> },
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);

export function App() {
  const session = useSessionQuery();
  const setSession = useSetSession();
  const qc = useQueryClient();

  if (session.isPending) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner label="Loading HostDock Admin" />
      </div>
    );
  }
  if (session.error) {
    return (
      <AuthLayout product="Admin">
        <ErrorState error={session.error} onRetry={() => void session.refetch()} />
      </AuthLayout>
    );
  }
  if (!session.data) {
    return (
      <AuthLayout product="Admin">
        <LoginScreen api={api.account} title="Sign in to HostDock Admin" onAuthenticated={setSession} />
      </AuthLayout>
    );
  }
  if (session.data.restriction) {
    return (
      <RestrictedGate
        product="Admin"
        restriction={session.data.restriction}
        api={api.account}
        onResolved={() => void qc.invalidateQueries({ queryKey: SESSION_KEY })}
        onLogout={async () => {
          await api.account.logout().catch(() => undefined);
          client.setCsrfToken(null);
          setSession(null);
        }}
      />
    );
  }
  return (
    <SessionProvider session={session.data}>
      <RouterProvider router={router} />
    </SessionProvider>
  );
}
